<template>
  <ModalFormulario
    :model-value="modelValue"
    @update:model-value="(val) => emit('update:modelValue', val)"
    titulo="Nova Imagem Avulsa"
    :loading="isUploading"
    texto-confirmar="Enviar"
    @confirmar="enviarImagem"
  >
    <div class="row g-3">
      <div class="col-12">
        <CampoInput
          id="descricaoImagemAvulsa"
          label="Descrição"
          v-model="descricao"
          placeholder="Ex: Selo de qualidade para relatório"
          descricao-informativa="Informe para que finalidade esta imagem será usada."
          required
        />
      </div>

      <div class="col-12">
        <label class="form-label d-block">
          Arquivo de Imagem <span class="text-danger">*</span>
        </label>
        <div
          class="upload-area"
          :class="{ 'drag-over': isDragOver, 'has-file': arquivoSelecionado, 'is-invalid': erroArquivo }"
          @dragover.prevent="isDragOver = true"
          @dragleave="isDragOver = false"
          @drop.prevent="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".png,.jpg,.jpeg,.gif,.svg"
            class="d-none"
            @change="handleFileSelect"
          />

          <template v-if="!arquivoSelecionado && !previewUrl">
            <i class="bi bi-cloud-arrow-up fs-1 text-muted mb-2"></i>
            <p class="text-muted small mb-1">Clique ou arraste uma imagem</p>
            <p class="text-muted small">Formatos: PNG, JPG, GIF, SVG | Máx: 10MB</p>
          </template>

          <template v-if="previewUrl">
            <img :src="previewUrl" alt="Preview" class="preview-img" />
            <div class="mt-2">
              <span class="badge bg-secondary-subtle text-secondary-emphasis me-1">
                {{ nomeArquivo }}
              </span>
              <span class="badge bg-info-subtle text-info-emphasis">
                {{ larguraDetectada }}x{{ alturaDetectada }}px
              </span>
            </div>
          </template>
        </div>

        <div v-if="erroArquivo" class="text-danger small mt-1">
          <i class="bi bi-exclamation-circle me-1"></i>{{ erroArquivo }}
        </div>
      </div>
    </div>
  </ModalFormulario>
</template>

<script setup>
import { ref } from 'vue'
import api from '@/services/api'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'

const props = defineProps({
  idEmpresa: {
    type: Number,
    required: true
  },
  modelValue: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['imagem-uploaded', 'update:modelValue', 'show-notification'])

const descricao = ref('')
const arquivoSelecionado = ref(null)
const previewUrl = ref(null)
const nomeArquivo = ref('')
const larguraDetectada = ref(0)
const alturaDetectada = ref(0)
const isDragOver = ref(false)
const isUploading = ref(false)
const erroArquivo = ref('')
const fileInput = ref(null)

const handleDrop = (e) => {
  isDragOver.value = false
  const files = e.dataTransfer.files
  if (files.length > 0) processarArquivo(files[0])
}

const handleFileSelect = (e) => {
  const file = e.target.files[0]
  if (file) processarArquivo(file)
}

const processarArquivo = (file) => {
  erroArquivo.value = ''
  arquivoSelecionado.value = file
  nomeArquivo.value = file.name

  // Gerar preview local
  const reader = new FileReader()
  reader.onload = (e) => {
    previewUrl.value = e.target.result
    // Detectar dimensões via Image
    const img = new Image()
    img.onload = () => {
      larguraDetectada.value = img.width
      alturaDetectada.value = img.height
    }
    img.src = e.target.result
  }
  reader.readAsDataURL(file)
}

const enviarImagem = async () => {
  if (!descricao.value.trim()) {
    emit('show-notification', { message: 'Informe uma descrição para a imagem.', type: 'warning' })
    return
  }
  if (!arquivoSelecionado.value) {
    emit('show-notification', { message: 'Selecione um arquivo de imagem.', type: 'warning' })
    return
  }

  // Validação client-side de tamanho (10MB)
  if (arquivoSelecionado.value.size > 10 * 1024 * 1024) {
    emit('show-notification', { message: 'O arquivo excede o tamanho máximo de 10MB.', type: 'warning' })
    return
  }

  isUploading.value = true
  erroArquivo.value = ''

  try {
    const formData = new FormData()
    formData.append('Arquivo', arquivoSelecionado.value)
    formData.append('TipoImagem', 'AVULSO')
    formData.append('IdEmpresa', props.idEmpresa.toString())
    formData.append('Descricao', descricao.value.trim())

    const response = await api.post('/EmpresaImagens/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    emit('imagem-uploaded', response.data)
    emit('update:modelValue', false)
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao enviar imagem.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isUploading.value = false
  }
}

</script>

<style scoped>
.upload-area {
  border: 2px dashed var(--bs-border-color);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: var(--bs-primary);
  background-color: var(--bs-primary-bg-subtle);
}

.upload-area.has-file {
  border-style: solid;
  border-color: var(--bs-success);
}

.upload-area.is-invalid {
  border-color: var(--bs-danger);
}

.preview-img {
  max-width: 100%;
  max-height: 180px;
  object-fit: contain;
  border-radius: 4px;
}
</style>
